import type { Locale } from './types'
import { zhParadigms } from './paradigms/zh'
import { zhLabDemos } from './lab-demos/zh'
import { zhIndustryDemos } from './industries/zh'
import { zhMethod } from './method/zh'
import { zhCases } from './cases/zh'
import { zhPlaybook } from './playbook/zh'

export const zh: Locale = {
  meta: {
    title: 'Orion AI',
    description: 'Ontology · 企业 AI 的语义底座。',
    pageTitle: 'Orion AI · 企业 AI 的三段地图',
  },
  langSwitch: {
    ariaLabel: '切换语言',
  },
  nav: {
    home: '行业',
    paradigms: '应用范式',
    demo: '演示',
    lab: '实验场',
    industries: '行业',
    method: '方法论',
    cases: '成功案例',
    playbook: '操作手册',
    menuAriaLabel: '主菜单',
  },
  industryDemos: zhIndustryDemos,
  header: {
    enterDemo: '进入演示 →',
    backHome: '← 返回主页',
    logoLabel: '返回主页',
  },
  home: {
    hero: {
      titleLine1: 'Ontology',
      titleLine2: '企业 AI 的语义底座',
    },
    whatIs: {
      sectionTag: '01 · 是什么',
      titleA: 'Ontology 是企业的',
      titleSpan: '语义操作系统',
      oneLineLabel: '一句话',
      oneLineBefore: '把企业的 ',
      oneLineObj: '对象',
      oneLineRel: '关系',
      oneLineCon: '约束',
      oneLineAct: '动作',
      oneLineAfter: ' 形式化, 让人和 AI 共享同一种"真理"。',
      solvesLabel: '解决什么',
      solvesItems: [
        '数据孤岛: 同一"客户" / "已交付" / "等效" 在不同系统里指代不同',
        '语义漂移: 阈值改一处, Skill / RAG / MCP 跟不上',
        'SOP 僵化: Skill 写死了一种走法, 换个目标就重做',
        '不可追溯: AI 决策无法回溯到对象、规则、数据源',
      ],
      ontologyAiLabel: 'Ontology + AI = 自动编排',
      ontologyAiBody1:
        '基于 ontology 解决问题时, AI Agent 不按预设流程办事 — ',
      ontologyAiSpan: '它读 ontology 现场推导路径',
      ontologyAiBody2:
        '。目标变,路径变;数据变,路径变;约束变,路径变。每一个节点和连线都是当下合成的, 不是事先存好的 SOP。',
    },
    upgrades: {
      sectionTag: '02 · 在企业 AI 转型中能提升什么',
      titleA: '从"能用"到',
      titleSpan: '可托付',
      titleB: '的四个跃迁',
      items: [
        {
          title: '确定性',
          body: '同一个问题 → 同一组答案。Ontology 把"等效" / "已交付" / "客户" 等概念在所有 Skill / RAG / MCP 里钉成同一定义。',
        },
        {
          title: '稳定性',
          body: 'AI 不再依赖临时拼凑的 prompt。目标 + 数据 + 约束驱动路径合成,工程师改一处约束,全图重传播。',
        },
        {
          title: '可观察性',
          body: '任何 AI 结论都可展开成对象 → 关系 → 约束 → 数据源的可解释链。事故复盘从"查日志"升级到"看图谱"。',
        },
        {
          title: '可治理性',
          body: '本体是企业的"AI 宪法"。修改它要走变更审批,像修 ISO 流程一样郑重 — AI 行为可治理,不只可调试。',
        },
      ],
    },
    industriesSection: {
      sectionTag: '03 · 11 个行业的 Ontology 应用',
      titleA: '每个行业都有',
      titleSpan: '自己的语义底座',
      leadA: '本体的对象 / 关系 / 约束 / 动作不同, 但',
      leadSpan: '"AI 在底座上现场推路径"',
      leadB: '这件事是通用的。',
      labels: {
        applications: '典型应用',
        coreObjects: '本体核心对象',
        value: 'Ontology 价值',
        scenario: '典型场景',
      },
    },
    bottomCta: {
      sectionTag: '想看真实工作流',
      titleLine1: '打开 Orion AI 三段演示',
      titleLine2: 'L1 / L2 / L3 的差距, 一屏看完',
      body: '基于陆上石油设备紧急采购的真实场景, 用聊天 / 调用栈 / 工作流三种交互呈现 ontology 的核心价值。',
      button: '进入演示',
    },
  },
  demoHero: {
    tag: '陆上石油天然气设备采购',
    titleLine1: '企业 AI 的',
    titleLine2: '三段地图',
    para1: {
      arrowBefore: '',
      midA: '通识 LLM',
      midB: '企业 AI 主流栈 (RAG + MCP + Skill)',
      midC: 'AI + Ontology',
      arrowAfter: '。',
    },
    para2: {
      lead: '前两段都在"给 LLM 做加法", 第三段是',
      spanA: '范式跃迁',
      tail: ' — AI 不照流程办事, 它读 ontology 现场推导路径。',
    },
    storyCard: {
      alertTag: '报警 K-3041',
      alertMeta: '02:14 · CGS-3 · P-1023A',
      body:
        '凌晨, 塔基姆油气田中央处理站。高含硫天然气脱硫装置进料泵机封一级泄漏。 2 周到货的 RFQ 推上去, 三家投标各有偏差。',
      bodyHaze: '同一个问题, Orion AI 在三个版本会怎么回答?',
    },
  },
  industries: [
    {
      id: 'finance',
      code: 'FIN',
      name: '金融与银行',
      nameEn: 'Finance & Banking',
      applications: ['AML / KYC', '客户 360', '风险归因', '反欺诈图谱'],
      coreObjects: ['客户', '账户', '交易', '风险事件', '监管规则', '黑名单'],
      benefit:
        '把 "客户" / "交易" / "账户" / "风险事件" 语义统一到同一图谱 — 风控、合规、客户经理共享真理底座。一次政策变更全图传播,新合规规则自动应用到所有在途交易。',
      scenario:
        '央行升息 25 bp → ontology 自动重算所有按揭客户的 PD(违约概率),标记 12,400 个高风险账户,触发 KYC 再审 + 自动通知对应客户经理 + 同步给监管报送。',
    },
    {
      id: 'insurance',
      code: 'INS',
      name: '保险',
      nameEn: 'Insurance',
      applications: ['理赔自动化', '欺诈检测', '核保评估', '产品 / 条款管理'],
      coreObjects: ['被保人', '保单', '索赔事件', '医疗记录', '车辆 / 财产', '反欺诈规则'],
      benefit:
        '"被保人" / "保单" / "事件" / "医疗记录" / "理赔规则" 关联到统一本体。同一理赔多路径并行推理 (医院诊断 + 警方报告 + 历史索赔 + 反欺诈规则),AI 自动合成处理动作。',
      scenario:
        '一笔车险报案 → ontology 同时调用医院诊断 + 警方现场报告 + 历史出险记录 + 反欺诈模型,5 秒给出"建议放赔 / 复核 / 拒赔" 三档建议 + 可解释推理链。',
    },
    {
      id: 'automotive',
      code: 'MFG',
      name: '汽车与制造业',
      nameEn: 'Automotive & Manufacturing',
      applications: ['BOM 主数据', '质量追溯', '供应链可视化', '数字孪生'],
      coreObjects: ['零件', '批次', '工艺路线', '装配关系', '整车', '缺陷记录'],
      benefit:
        '零件、批次、工艺、整车、缺陷、回溯链条全部本体化。质量问题发生时,ontology 沿装配 + 物料 + 工艺链路反向回溯影响范围;新规标准变更全图重传播。',
      scenario:
        '某零件批次质量异常 → ontology 沿 BOM 反向追溯到 86 辆已售整车 + 12 个在装配线在制品,自动生成召回名单 + 补救 SOP + 供应商索赔依据。',
    },
    {
      id: 'retail',
      code: 'RTL',
      name: '零售与消费品',
      nameEn: 'Retail & Consumer',
      applications: ['商品主数据', '推荐 / 个性化', '动态补货', '促销优化'],
      coreObjects: ['SKU', '品类 / 品牌', '客户画像', '渠道', '库存', '促销规则'],
      benefit:
        '商品 / SKU / 品牌 / 品类 / 客户偏好统一语义。新品上架、促销策略、价格变动改一处, 全渠道 (实体店 / 电商 / 社交 / 第三方) 自动同步,避免渠道间数据漂移。',
      scenario:
        '新品上架 → ontology 自动推到 5 个电商渠道 + 32 家直营店 + 社交社群 + 第三方分销,价格策略 / 描述 / 配图 / 库存全部同步,无需逐平台手工配置。',
    },
    {
      id: 'healthcare',
      code: 'HLT',
      name: '医疗健康',
      nameEn: 'Healthcare',
      applications: ['临床决策支持', '患者画像', '医学知识图谱', '药物相互作用'],
      coreObjects: ['患者', '症状', '诊断', '药物', '检查项', '临床指南'],
      benefit:
        '患者、症状、诊断、治疗、药物、相互作用关联到统一本体。新临床指南发布时, 所有匹配患者的治疗推理路径自动应用;跨科室共享同一医学语义,告别系统孤岛。',
      scenario:
        '急诊高热患者就诊 → ontology 综合 EHR 病史 + 当前症状 + 检查结果, 现场推出鉴别诊断顺序 + 必要检查列表 + 用药禁忌 + 转诊建议,给医生 1 分钟决策窗。',
    },
    {
      id: 'legal',
      code: 'LGL',
      name: '法律与合规',
      nameEn: 'Legal & Compliance',
      applications: ['合同分析', '法律检索', '合规审查', '监管追踪'],
      coreObjects: ['法条', '判例', '合同', '当事人', '合规检查项', '监管文件'],
      benefit:
        '法条、判例、合同条款、监管规则形式化为本体。新法规出台时, ontology 自动识别影响哪些现存合同 / 流程 / 业务线;合规审查从"人工逐条核查"升级到"图谱自动覆盖"。',
      scenario:
        '新数据合规法颁布 → ontology 自动扫描 1,200 份在用合同 + 86 个内部流程,标记 87 份需修订条款 + 12 个流程需重设计,并自动起草补充协议建议。',
    },
    {
      id: 'logistics',
      code: 'LOG',
      name: '物流与供应链',
      nameEn: 'Logistics & Supply Chain',
      applications: ['订单端到端跟踪', '异常归因', '需求预测', '运输优化'],
      coreObjects: ['订单', 'SKU', '物流商', '关税 / 单据', '运单', '客户 SLA'],
      benefit:
        '订单、物流商、关税、运单、库存、客户承诺 SLA 都在同一图谱。一次"订单异常"问题, ontology 沿端到端链路定位根因 + 责任方 + 补救方案;不再需要人工跨系统核对。',
      scenario:
        '客户 X 工单 "货没到" → ontology 串起 SAP / WMS / TMS / 海关 4 个系统数据, 10 秒定位卡在 "巴西海关 ANVISA 证书缺失",给出补救路径 + 责任方 + ETA。',
    },
    {
      id: 'realestate',
      code: 'REA',
      name: '房地产',
      nameEn: 'Real Estate',
      applications: ['物业管理', '市场估值', '租赁优化', '资产合规'],
      coreObjects: ['物业 / 房源', '租客', '估值因子', '市场数据', '政策约束', '运维记录'],
      benefit:
        '物业、房源、租客、估值因素、市场数据、政策约束关联本体化。市场政策变化 (限购 / 利率 / 税收) → ontology 全部资产估值自动重算 + 受影响合同自动识别。',
      scenario:
        '某市限购令更新 → ontology 全部 3,400 套资产估值自动重算,识别 124 套受影响房源 + 47 份租约需重谈,触发客户通知 + 财务计提调整,2 小时完成全公司响应。',
    },
    {
      id: 'energy',
      code: 'ENG',
      name: '能源行业',
      nameEn: 'Energy (Power / Oil & Gas / Renewables)',
      applications: ['资产管理 / 设备采购', 'HSE 合规', '能源调度 / 交易', '碳追踪'],
      coreObjects: ['设备', '工况', '标准 (API / NACE / ISO)', '供应商 AVL', '合规规则', 'MOC 记录'],
      benefit:
        '设备、工况、标准 (API / NACE / ISO)、供应商 AVL、合规规则统一本体 — 这是 Orion 当前 demo 的场景。标准升级一处, 全图重传播识别受影响资产 + 在途采购。',
      scenario:
        '海上平台 H₂S 浓度突增 → ontology 触发材质合规重检 + AVL 紧急供应商搜索 + MOC 变更流程 + HSE 报告生成,24 小时给出三档处置方案 + 风险等级。',
    },
    {
      id: 'education',
      code: 'EDU',
      name: '教育与培训',
      nameEn: 'Education & Training',
      applications: ['课程知识图谱', '个性化学习路径', '能力评估', '内容自动生成'],
      coreObjects: ['知识点', '技能', '课程', '学员画像', '学习目标', '评估指标'],
      benefit:
        '知识点、技能、课程、学员画像、目标形式化。学员目标变化 (转岗 / 升级 / 兴趣) → AI 在本体上重新合成个性化学习路径, 而不是从固定课程表里选。',
      scenario:
        '学员从产品经理转岗数据分析 → ontology 基于现有技能 + 目标岗位差距,现场推荐 18 门课 + 4 个实战项目 + 3 本必读书,排成 12 周个性化路径。',
    },
    {
      id: 'government',
      code: 'GOV',
      name: '政府与公共服务',
      nameEn: 'Government & Public Services',
      applications: ['跨部门数据共享', '政策影响分析', '一网通办市民服务', '应急响应'],
      coreObjects: ['公民', '企业', '政策', '事件', '地理位置', '部门职责'],
      benefit:
        '公民、企业、政策、事件、地理位置、部门职责关联到统一本体。新政策出台时, 影响的群体 / 部门 / 流程 / 服务事项自动可视化;一网通办从"打通系统"升级到"共享语义"。',
      scenario:
        '某市新出台"高龄津贴 +20%" → ontology 自动识别 42,800 名符合条件公民, 同时通知社区 / 街道 / 财政 / 银行四个部门,排出发放计划 + 异议处理流程。',
    },
  ],
  layers: {
    L1: {
      shortLabel: 'L1 · LLM',
      techName: '通识 LLM',
      oneLiner: '模型权重即全部知识 — 会聊, 但不懂你的世界',
      newCapability: '会聊',
      fatalGap: '没有企业数据 — 任何具体决策都答得像百科全书',
    },
    L2: {
      shortLabel: 'L2 · 企业 AI 主流栈',
      techName: 'RAG + MCP + Skill',
      oneLiner: '给 LLM 加文档、加工具、加 SOP — 会读会做会照章办事',
      newCapability: '会读 / 会做 / 照 SOP',
      fatalGap:
        '流程是写死的 · Skill 之间各说各话 · 阈值改一处要逐 Skill 改 · 看不见 RFQ 之外的世界',
    },
    L3: {
      shortLabel: 'L3 · +Ontology',
      techName: 'AI + Ontology (语义底座)',
      oneLiner: 'AI 不照流程办事 — 它读 ontology 现场推导路径',
      newCapability: '会想 / 会自己编排',
      fatalGap: null,
    },
  },
  layerSection: {
    fatalGapLabel: '致命缺口',
    pivotLabel: '拐点',
    pivotBody1: 'Ontology 不是再加一个能力, 而是给前两层一个共同的',
    pivotSemanticSpan: '语义底座',
    pivotBody2: ' — AI Agent 在底座上',
    pivotPathSpan: '现场推导路径',
    pivotBody3: ', 而不是按预先写好的 SOP 走。 这才让企业要的',
    pivotFourQualitiesSpan: '确定性 / 稳定性 / 可观察性 / 可治理性',
    pivotBody4: '真正可达。',
    highlightBadge: '高光时刻',
  },
  l1: {
    header: 'Orion · L1 LLM only',
    noDataBadge: '无企业数据接入',
    rerunButton: '▶ 重新运行',
    inputPlaceholder: '问 Orion ...',
    protocolTag: 'chat completion',
    bubbleNoSource: '无企业数据 · 通识知识',
    chat: [
      {
        who: 'user',
        meta: 'M. Chen · 09:31',
        text: '我们在选三家机封, A 报 18.2 万 Plan 53A, B 报 35.6 万 SS316 现货, C 报 22.4 万 NACE 证书 2024-08。介质是高硫天然气。怎么选?',
      },
      {
        who: 'ai',
        meta: 'Orion · L1',
        text: '高含硫天然气工况选机封需遵循 API 682 标准, 一般考虑材质抗硫性 (NACE MR0175)、API plan 选型 (与介质蒸汽压相关)、证书版本时效。A 的 Plan 53A 是非加压方案, 适用于低蒸汽压介质; B 的 SS316 是奥氏体不锈钢, 在含 H₂S 高氯环境需谨慎评估; C 的证书版本建议核实。建议您结合具体工况参数进行评估。',
      },
      {
        who: 'user',
        meta: 'M. Chen · 09:32',
        text: '...这是百科, 我要的是哪一家。',
      },
    ],
  },
  l2: {
    rerunButton: '▶ 重新运行',
    tabs: {
      rag: { label: 'RAG', caption: '让 AI 读企业文档' },
      mcp: { label: 'MCP', caption: '让 AI 调企业系统' },
      skill: { label: 'Skill', caption: '让 AI 照 SOP 工作' },
    },
    sharedLimitationLabel: '共同天花板',
    sharedLimitation:
      '三件套之间各说各话; Skill SOP 写死, 看不见 RFQ 之外的供应商; 阈值升级要逐 Skill 改; 同一问题换个目标就要重做 Skill。',
    rag: {
      chat: [
        {
          who: 'user',
          meta: 'M. Chen · 09:35',
          text: '按 P-1023 设备规格书对比这三家。',
        },
        {
          who: 'ai',
          meta: 'Orion · L2',
          text: '据 [DS-OF-TQM-CGS3-U200-P1023.pdf §4.2]: 该泵要求 API 682 Plan 53B + Inconel 625 或同等, NACE MR0175 现行版。据 [NACE/ISO 15156-3:2025-03 §A.2.1.3]: H₂S 分压 > 0.3 kPa 且 Cl⁻ > 10000 mg/L 时禁用 SS316。三家投标与规格书均存在偏差, 建议逐项核对。',
        },
      ],
      citationsLabel: '检索到的引用 · 3 篇',
      citations: [
        {
          id: 'c1',
          ref: 'DS-OF-TQM-CGS3-U200-P1023.pdf §4.2',
          quote: '该泵要求 API 682 Plan 53B + Inconel 625 或同等, NACE MR0175 现行版...',
        },
        {
          id: 'c2',
          ref: 'NACE/ISO 15156-3:2025-03 §A.2.1.3',
          quote: '当 H₂S 分压 > 0.3 kPa 且 Cl⁻ > 10,000 mg/L 时, 禁用 SS316 / 316L...',
        },
        {
          id: 'c3',
          ref: '集团 PA-2024-117 §8.4',
          quote: '紧急件 SLA 7-14 天, 标的金额 < 30 万走油田分公司审批...',
        },
      ],
      inputPlaceholder: '按 P-1023 设备规格书对比这三家 ...',
      protocolTag: 'retrieve + read',
      hasCitationBadge: '引用企业文档',
    },
    mcp: {
      promptMeta: 'M. Chen 09:42',
      userPrompt: '核对 A / B / C 三家投标与 P-1023 规格书的符合性, 并查 AVL 状态。',
      callStackLabel: '调用栈',
      protocolNote: 'MCP / model context protocol',
      toolCalls: [
        { id: 't1', system: 'EAM', description: 'getAsset("P-1023") · 拉工况基线', resultLine: 'H₂S 12.6% / Cl⁻ 18,300 / 21 MPa / 95°C / VP 0.42' },
        { id: 't2', system: 'SAP', description: 'getRFQ("29117") · 拉三家投标', resultLine: 'A: Plan 53A · B: SS316 现货 · C: NACE 2024-08' },
        { id: 't3', system: 'AVL', description: 'queryStatus([A,B,C], scope=[group,field])', resultLine: 'A ✓✓ · B ✓✓ · C group ✓ field SUSPENDED' },
        { id: 't4', system: 'QMS', description: 'getCertExpiry("C", "NACE MR0175")', resultLine: 'v2024-08 < current v2025-03' },
        { id: 't5', system: 'PI', description: 'getOperatingTrend("P-1023", last_30d)', resultLine: '实测峰值 H₂S 14.1%, 高于 RFQ 名义值' },
      ],
      matrixTitle: '合规矩阵 · 自动生成',
      matrixBadge: '无完全合规投标',
      matrixHeaders: { item: '项目', note: '说明' },
      matrix: [
        { item: 'API plan', a: 'block', b: 'pass', c: 'pass', note: 'A: 53A 非加压, VP 0.42 需 53B+' },
        { item: '材质', a: 'pass', b: 'block', c: 'pass', note: 'B: SS316 在酸性服务禁用' },
        { item: 'NACE 证书', a: 'pass', b: 'pass', c: 'block', note: 'C: 2024-08 已过版' },
        { item: 'AVL 集团', a: 'pass', b: 'pass', c: 'pass', note: '' },
        { item: 'AVL 油田', a: 'pass', b: 'pass', c: 'block', note: 'C: 2024-11 暂停' },
        { item: '交期', a: 'pass', b: 'block', c: 'pass', note: 'B: 21 天 > SLA 14 天' },
      ],
    },
    skill: {
      skillPrefix: 'Skill · ',
      version: 'v2.4.0',
      progressBadge: '8 / 8 完成',
      stepRowPrefix: '[{idx}/08]',
      steps: [
        { index: 1, title: '提取工况基线' },
        { index: 2, title: '拉取 RFQ 与三家投标' },
        { index: 3, title: '对照规格书 (9 项)' },
        { index: 4, title: 'AVL 三级查询' },
        { index: 5, title: '证书有效期校验' },
        { index: 6, title: 'MOC 触发判定' },
        { index: 7, title: '历史索赔检索' },
        { index: 8, title: '生成合规矩阵 + 推荐' },
      ],
      reportLabel: '标准化报告 · 可签字归档',
      recommendation:
        '当前无完全合规投标。若以"最少 MOC"为决策原则: 推荐 B + MOC-FAST (材质升级); 风险: 21 天交期 vs 备机寿命 9-12 天。若以"最快交期"为决策原则: 推荐 A + MOC-FAST (Plan 升级) + 厂家工艺确认函。',
      pendingDecision: '请首席工程师 / 部门主管 选择决策原则并联签。',
      pendingTag: '等待人工',
    },
  },
  l3: {
    selectGoalLabel: '① 选择目标',
    selectGoalHint: '同一本体, 不同目标 → 完全不同的路径',
    adjustConditionLabel: '② 调整工况',
    adjustConditionHint: '拖动 → 工作流实时重新合成',
    constraintsBadgeTpl: '{count} 条强约束',
    slider: {
      h2sLabel: 'H₂S (%)',
      h2sHintHot: '酸性服务 — NACE 强制',
      h2sHintCold: '非酸性服务',
      clLabel: 'Cl⁻ (mg/L)',
      clHintHot: '高氯 — 禁奥氏体',
      clHintCold: '低氯',
      vpLabel: 'VP-ratio',
      vpHintHot: '高蒸汽压 — API 53B+',
      vpHintCold: '低蒸汽压',
    },
    whiteboardTitle: 'Ontology 工作流',
    nodesEdgesTpl: '· {n} 节点 · {e} 边',
    attributionLabel: '反向归因',
    attributionClear: '清除 ✕',
    attributionHint: '点节点 → 反向归因 · dagre layout',
    bottomCaption: '所有节点 / 连线都不是事先写好的 — 由 ontology 按目标 + 工况现场合成。',
    pinButton: '一键放行 →',
    goals: [
      { id: 'compliance', label: '① 合规选采', caption: '为 P-1023 选合规机封 2 周到货' },
      { id: 'uptime', label: '② 不停产', caption: '备机能撑多久, 怎么过渡' },
      { id: 'cost', label: '③ 总成本最低', caption: '含设备 + 停产 + 事故概率' },
    ],
    paths: {
      compliance: {
        goalLabel: 'G1 · 合规选采',
        goalDetail: '"为 P-1023 选合规机封, 2 周到货"',
        objLabel: 'Equipment::P-1023',
        objDetail: 'deref → hasOperatingCondition + hasDesignSpec',
        infCondLabel: '工况推理',
        infCondDetailTpl: 'H₂S {h2s} · Cl⁻ {cl} · VP-ratio {vp}',
        rfqLabel: 'RFQ::29117',
        rfqDetail: '三家投标 A / B / C 已归档, 解析其规格/价/期',
        cMatLabel: 'R-MAT-NACE-21',
        cMatDetail: '酸性服务 + 高氯 → 禁 SS316',
        cMatCitation: 'NACE / ISO 15156-3',
        cPlanLabel: 'R-PLAN-VP-08',
        cPlanDetailTpl: 'VP-ratio {vp} > 0.3 → 禁 Plan 53A',
        cPlanCitation: 'API 682 §6.1.2.5',
        cNoneLabel: '工况温和',
        cNoneDetail: '无强约束触发, 候选直接进入价 / 期比较',
        candLabelViol: '候选评估 (有违规)',
        candLabelOk: '候选评估 (全合规)',
        candDetailOk: '价 / 期: A 18.2 万 / 7d · B 35.6 万 / 21d · C 22.4 万 / 14d',
        candViolPieceMat: 'B 违材质',
        candViolPiecePlan: 'A 违 plan',
        candViolPieceCert: 'C 违证书 + 油田 AVL',
        infCovLabel: 'R-COV-09',
        infCovDetail: '跨 RFQ 主动覆盖 — 发现 D, 历史交期均值 15.8 ± 1.4 天',
        actLabelViol: 'expandRfq + raiseMOC',
        actLabelOk: 'awardBest',
        actDetailViol: '扩 RFQ 邀 D · 启 MOC-Fast-Track · 备机 P-1023B 强化巡检',
        actDetailOk: '基于价 / 期 / 历史交付 → 直接判 A 中标 · 备案',
      },
      uptime: {
        goalLabel: 'G2 · 保证不停产',
        goalDetail: '"备机能撑多久, 怎么过渡"',
        objLabel: 'P-1023B (备机)',
        objDetail: 'standbyOf P-1023A · 历史磨损 / 上次大修 2024-09',
        piLabel: 'PI 实时数据',
        piDetail: '端面温度 92°C · 振动 3.2 mm/s · 趋势上升',
        infLifeLabel: '剩余寿命估算',
        infLifeDetailTpl: 'H₂S {h2s} 腐蚀因子 {factor} → {lo}-{hi} 天',
        cSlaLabel: 'R-BACKUP-SLA-11',
        cSlaDetail: '备机寿命 < 最快合规交期 → 必须并联强化巡检 + 限运行',
        cSlaCitation: '集团 EQ-BACKUP-SLA',
        candLabel: '过渡方案集',
        candDetail: '巡检强化 4h/次 · D 定制 16d · 局部降负荷 80% · 临停 16h 维护',
        actLabel: 'schedInsp + degrade',
        actDetail: '强化巡检 + 降负荷 80% + 并扩 RFQ 邀 D · 无需 MOC',
      },
      cost: {
        goalLabel: 'G3 · 总成本最低',
        goalDetail: '"含设备 + 停产 + 事故概率"',
        costModelLabel: 'CostModel',
        costModelDetail: '期望总成本 = 价格 + 停产损失 × p + HSE 罚款 × p',
        riskModelLabel: 'RiskModel',
        riskModelDetail: '事故概率函数 p(工况, 材质, plan, 历史索赔)',
        infMcLabel: '蒙特卡洛 (10k 次)',
        infMcDetailTpl: '工况 (H₂S {h2s} / Cl⁻ {clk}k / VP {vp}) → A: {a} · B: {b} · D: {d} 万',
        cMocLabel: 'R-MOC-MANDATORY-01',
        cMocDetailNeeded: 'D 涉规格偏差 → 必走 MOC, 计入 12k 流程成本',
        cMocDetailNotNeeded: '所有候选无偏差 → 无需 MOC',
        cMocCitation: '集团 HSE-MOC-2024',
        candLabel: '排序',
        candDetailTpl: '最优 = {best} ({cost} 万) · 含事故概率与 MOC 成本',
        actLabelAggressive: 'expandRfq + MOC + alert',
        actLabelMild: 'awardBest + report',
        actDetailAggressiveTpl: '扩 RFQ 邀 D · 预算锁定 {d} 万 ± 6 万 · 同步报 CFO',
        actDetailMildTpl: '直接判 A 中标 · 预算 {a} 万 · 备案',
      },
    },
    advantagesTitle: 'Ontology 的 8 大企业级优势',
    advantages: [
      { title: '动态编排', body: '路径不写死 — 同一本体, 不同目标, 现场合成完全不同的执行链。' },
      { title: '跨域一致', body: '"材质等效" 在采购 / 工程 / 维修口径里指同一定义, 不再各说各话。' },
      { title: '可解释', body: '任一节点可展开 → 看到背后的对象、关系、约束、数据来源。' },
      { title: '可治理', body: '改一条规则 (如 H₂S 阈值), 所有 Skill / RAG / MCP 自动应用, 无需逐一改。' },
      { title: '主动认知', body: '事件流上跑约束 — 问题发生前就推送告警和替代方案。' },
      { title: '自我修正', body: '推理结果可被更高优先级规则反驳; 治理告警在你关掉强约束时出现。' },
      { title: '可组合', body: '同一本体支持无穷多个新目标 — 不需要重训模型, 不需要重做 Skill。' },
      { title: '知识迁移', body: '新场景接入本体即可继承历史智慧; 一个油田的经验自然流到下一个。' },
    ],
    kindLabels: {
      goal: '目标',
      object: '对象',
      inference: '推理',
      constraint: '约束',
      candidate: '候选',
      action: '动作',
    },
  },
  paradigms: zhParadigms,
  labDemos: zhLabDemos,
  method: zhMethod,
  cases: zhCases,
  playbook: zhPlaybook,
  lab: {
    sectionTag: '实验场',
    heroTitle: '可交互的 paradigm 实验场',
    heroSubtitle:
      '每个 paradigm 都有自己的演练空间。左边触发用户侧动作,右边看 ontology 如何响应。所有 demo 接到同一个语义底座 — 形态各异,管道一致。',
    sidebarTitle: 'Paradigms',
    tryItLabel: '去试一下 →',
    comingSoonLabel: '即将上线',
    comingSoonBody:
      '这个 paradigm 的场景已经设计好,会在后续 phase 落地。先去 Paradigms 页看完整介绍。',
    userActionLabel: '用户操作',
    ontologyResponseLabel: 'Ontology 反应',
    backToParadigmsLabel: '← 回到 paradigm 全集',
  },
  footer: {
    line: 'Orion AI · 企业 AI 的三段地图 · Phase 2 Preview',
    versionPrefix: 'v0.2.0 · ',
  },
}
